import { useQuery } from '@tanstack/react-query'
import { Phone, Trophy, Clock, Flame } from 'lucide-react'
import { api } from '../../lib/api'
import { Card } from '../../components/ui/Card'
import { StatCard } from '../../components/ui/StatCard'
import { Badge } from '../../components/ui/Badge'
import { LineChart } from '../../components/charts/LineChart'
import { EmptyState } from '../../components/ui/EmptyState'
import { Button } from '../../components/ui/Button'
import styles from './SdrDashboard.module.css'

interface DatiDashboard {
  sessioni_settimana: number
  score_medio_obiezioni: number
  minuti_allenamento_totali: number
  streak_giorni: number
  ultima_sessione: {
    id: string
    durataSecondi: number
    votoComplessivo: number | null
    createdAt: string
  } | null
  sessioni_rimanenti: number | null
  lead_consigliato: {
    id: string
    nome: string
    ruolo: string
    difficolta: number
  } | null
}

interface DatiProgressi {
  ultimi_30_giorni: Array<{ data: string; sessioni: number; score_medio: number }>
}

export function SdrDashboard() {
  const dashboard = useQuery({
    queryKey: ['sdr', 'dashboard'],
    queryFn: async () => (await api.get<{ dati: DatiDashboard }>('/sdr/dashboard')).data.dati,
  })

  const progressi = useQuery({
    queryKey: ['sdr', 'progressi'],
    queryFn: async () => (await api.get<{ dati: DatiProgressi }>('/sdr/progressi')).data.dati,
  })

  if (dashboard.isLoading) return <div className={styles.skeleton}>Caricamento…</div>
  if (dashboard.error || !dashboard.data) {
    return (
      <EmptyState
        titolo="Impossibile caricare la dashboard"
        descrizione="Verifica che il backend sia in esecuzione e di essere connesso."
      />
    )
  }

  const d = dashboard.data

  return (
    <div className={styles.wrap}>
      {/* KPI cards */}
      <div className={styles.griglia}>
        <StatCard
          etichetta="Sessioni settimana"
          valore={d.sessioni_settimana}
        />
        <StatCard
          etichetta="Score medio obiezioni"
          valore={d.score_medio_obiezioni.toFixed(1)}
          unita="/5"
        />
        <StatCard
          etichetta="Minuti totali"
          valore={d.minuti_allenamento_totali}
          unita="min"
        />
        <StatCard
          etichetta="Streak giorni"
          valore={d.streak_giorni}
        />
      </div>

      {/* Grafico andamento */}
      <Card titolo="Andamento ultimi 30 giorni">
        {progressi.data && progressi.data.ultimi_30_giorni.length > 0 ? (
          <LineChart
            dati={progressi.data.ultimi_30_giorni}
            chiaveX="data"
            chiaveY="score_medio"
          />
        ) : (
          <EmptyState
            titolo="Nessun dato"
            descrizione="Inizia ad allenarti per vedere il tuo andamento."
          />
        )}
      </Card>

      {/* Due colonne in basso */}
      <div className={styles.due}>
        <Card titolo="Lead consigliato">
          {d.lead_consigliato ? (
            <div className={styles.leadConsigliato}>
              <div className={styles.leadIcona}><Phone size={20} /></div>
              <div className={styles.leadDati}>
                <div className={styles.leadNome}>{d.lead_consigliato.nome}</div>
                <div className={styles.leadRuolo}>{d.lead_consigliato.ruolo}</div>
                <Badge variante={difficoltaBadge(d.lead_consigliato.difficolta)}>
                  {etichettaDifficolta(d.lead_consigliato.difficolta)}
                </Badge>
              </div>
              <Button taglia="sm" variante="secondary">
                Apri
              </Button>
            </div>
          ) : (
            <EmptyState
              titolo="Nessun lead disponibile"
              descrizione="Crea il tuo primo lead per iniziare ad allenarti."
            />
          )}
        </Card>

        <Card titolo="Stato account">
          <div className={styles.statoLista}>
            <div className={styles.statoRiga}>
              <span className={styles.statoLabel}><Flame size={14} /> Streak</span>
              <span className={styles.statoValore}>{d.streak_giorni} giorni</span>
            </div>
            <div className={styles.statoRiga}>
              <span className={styles.statoLabel}><Clock size={14} /> Allenamento</span>
              <span className={styles.statoValore}>{d.minuti_allenamento_totali} min</span>
            </div>
            <div className={styles.statoRiga}>
              <span className={styles.statoLabel}><Trophy size={14} /> Score obiezioni</span>
              <span className={styles.statoValore}>{d.score_medio_obiezioni.toFixed(1)}/5</span>
            </div>
            {d.sessioni_rimanenti != null && (
              <div className={styles.statoRiga}>
                <span className={styles.statoLabel}>Sessioni rimanenti</span>
                <Badge variante={d.sessioni_rimanenti > 0 ? 'success' : 'warning'}>
                  {d.sessioni_rimanenti}
                </Badge>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  )
}

function etichettaDifficolta(d: number): string {
  if (d === 1) return 'Facile'
  if (d === 2) return 'Medio'
  return 'Difficile'
}

function difficoltaBadge(d: number): 'success' | 'warning' | 'danger' {
  if (d === 1) return 'success'
  if (d === 2) return 'warning'
  return 'danger'
}
